import { create } from 'zustand';

/**
 * Store de MyOrganization (Zustand) — señal compartida de invalidación de `/v2/organizations/me`.
 *
 * NO guarda la organización: solo un contador de versión. `useMyOrganization` sigue siendo un
 * `useState` + `useEffect` por instancia (una copia del dato por componente), y ese aislamiento
 * era el bug: al cancelar la prueba desde `BillingPage`, su `reload()` refrescaba únicamente SU
 * copia; la de `OrgLayout` —layout persistente que pinta el banner de "prueba terminada" y que no
 * se remonta al navegar entre rutas hijas— seguía con la organización activa hasta un F5.
 *
 * Cada instancia de `useMyOrganization` observa `version` y la incluye en las dependencias de su
 * efecto de carga, así que un `invalidate()` refresca TODAS las instancias montadas a la vez.
 *
 * Por qué un contador y no la organización en el store: mover el dato aquí sería el refactor de
 * los 8 consumidores a una fuente única (lo correcto de verdad es React Query, con su caché y su
 * deduplicación) — un cambio transversal con su propio OpenSpec. Esta señal es el mínimo que
 * corrige el estado obsoleto sin tocar la API pública del hook.
 *
 * Coste conocido: N instancias montadas ⇒ N peticiones a `/me` por bump. Hoy N es 1 o 2 (el
 * layout y la página activa) y ya era así con cada `reload()`; la deduplicación llegará con la
 * migración a React Query.
 */
interface MyOrganizationState {
  /** Se incrementa en cada invalidación. Su valor no significa nada: solo el cambio. */
  version: number;

  /** Marca los datos de la organización como obsoletos en todas las instancias montadas. */
  invalidate: () => void;
}

export const useMyOrganizationStore = create<MyOrganizationState>((set) => ({
  version: 0,

  invalidate: () => set((state) => ({ version: state.version + 1 })),
}));
